<script setup lang="ts">
/**
 * Viewport diagnostic overlay。
 *
 * 啟用方式:URL 加上 `?debug=viewport`(任何頁、任何遊戲都吃)。
 *
 * 用途:Pixel / 小米 11 / iOS Safari 等 mobile 瀏覽器,看不到 devtools,
 * 但又懷疑 `--host-vw` / `--host-vh` 算錯。把所有 viewport 相關數值即時印
 * 在右上角:
 *   - window.innerWidth / innerHeight
 *   - visualViewport.width / .height
 *   - document.documentElement.clientWidth / .clientHeight
 *   - --host-vw / --host-vh (CSS var,Math.max 後)
 *   - --card-w (computed)
 *   - .responsive-card 實際 boundingRect 寬高
 *   - matchMedia '(max-width: 768px)' 結果
 *   - DPR
 *
 * 60fps 重算(requestAnimationFrame),抓 URL bar 折疊瞬間。
 * 永遠 z-index 最高、pointer-events: none → 不擋操作。
 */
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { APP_ENV } from '@/runtimeConfig'

const enabled = new URLSearchParams(window.location.search).get('debug') === 'viewport'

interface Snapshot {
  innerW: number
  innerH: number
  vvW: number
  vvH: number
  clientW: number
  clientH: number
  hostVw: string
  hostVh: string
  cardW: string
  cardH: string
  cardRectW: number
  cardRectH: number
  mqMobile: boolean
  dpr: number
  ua: string
}

/** Build / 環境資訊 — 一次性的不需 RAF 重算 */
const viteMode = APP_ENV || 'unknown'
const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '(no build time)'

const snap = ref<Snapshot | null>(null)
let rafId = 0
let running = true

function sample(): Snapshot {
  const vv = window.visualViewport
  const root = document.documentElement
  const card = document.querySelector('.responsive-card') as HTMLElement | null
  const cardRect = card?.getBoundingClientRect()
  const cs = card ? window.getComputedStyle(card) : null
  return {
    innerW: window.innerWidth,
    innerH: window.innerHeight,
    vvW: vv?.width ?? -1,
    vvH: vv?.height ?? -1,
    clientW: root.clientWidth,
    clientH: root.clientHeight,
    hostVw: root.style.getPropertyValue('--host-vw') || '(unset)',
    hostVh: root.style.getPropertyValue('--host-vh') || '(unset)',
    cardW: cs?.getPropertyValue('--card-w')?.trim() || '(unset)',
    cardH: cs?.getPropertyValue('--card-h')?.trim() || '(unset)',
    cardRectW: cardRect?.width ?? -1,
    cardRectH: cardRect?.height ?? -1,
    mqMobile: window.matchMedia('(max-width: 768px)').matches,
    dpr: window.devicePixelRatio,
    ua: navigator.userAgent.slice(0, 80),
  }
}

function loop() {
  if (!running) return
  snap.value = sample()
  rafId = requestAnimationFrame(loop)
}

onMounted(() => {
  if (!enabled) return
  loop()
})

onBeforeUnmount(() => {
  running = false
  if (rafId) cancelAnimationFrame(rafId)
})

function fmt(n: number, digits = 2): string {
  if (n < 0) return '—'
  return n.toFixed(digits)
}
</script>

<template>
  <div v-if="enabled && snap" class="vp-debug">
    <div class="row build">
      <span>build</span><b>{{ viteMode }} {{ buildTime }}</b>
    </div>
    <div class="row">
      <span>inner</span><b>{{ snap.innerW }}×{{ snap.innerH }}</b>
    </div>
    <div class="row">
      <span>visualVp</span><b>{{ fmt(snap.vvW) }}×{{ fmt(snap.vvH) }}</b>
    </div>
    <div class="row">
      <span>htmlClient</span><b>{{ snap.clientW }}×{{ snap.clientH }}</b>
    </div>
    <div class="row">
      <span>--host-vw</span><b>{{ snap.hostVw }}</b>
    </div>
    <div class="row">
      <span>--host-vh</span><b>{{ snap.hostVh }}</b>
    </div>
    <div class="row">
      <span>--card-w</span><b>{{ snap.cardW }}</b>
    </div>
    <div class="row">
      <span>--card-h</span><b>{{ snap.cardH }}</b>
    </div>
    <div class="row">
      <span>cardRect</span><b>{{ fmt(snap.cardRectW) }}×{{ fmt(snap.cardRectH) }}</b>
    </div>
    <div class="row">
      <span>mq ≤768</span><b>{{ snap.mqMobile ? 'YES' : 'NO' }}</b>
    </div>
    <div class="row">
      <span>DPR</span><b>{{ snap.dpr }}</b>
    </div>
    <div class="ua">{{ snap.ua }}</div>
  </div>
</template>

<style scoped>
.vp-debug {
  position: fixed;
  top: 4px;
  left: 4px;
  z-index: 9999;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.78);
  color: #0f0;
  font-family: monospace;
  font-size: 10px;
  line-height: 1.35;
  padding: 4px 6px;
  border-radius: 4px;
  min-width: 150px;
  max-width: 240px;
  white-space: nowrap;
}
.row {
  display: flex;
  justify-content: space-between;
  gap: 6px;
}
.row span {
  color: #ff0;
}
.row b {
  color: #0f0;
  font-weight: normal;
}
.row.build {
  border-bottom: 1px solid #333;
  margin-bottom: 2px;
  padding-bottom: 2px;
}
.row.build b {
  color: #ff0;
  font-weight: bold;
}
.ua {
  color: #888;
  font-size: 9px;
  white-space: normal;
  word-break: break-all;
  margin-top: 3px;
  border-top: 1px solid #333;
  padding-top: 2px;
}
</style>
